import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DraftListItem from '../components/DraftListItem';
import { getUserDrafts } from '../services/api';

/**
 * Draft hub: lists the user's previous drafts and offers New Draft / Mock Draft buttons.
 * Clicking "My Drafts" in the Rankings toolbar lands here instead of
 * immediately creating a draft.
 */
function DraftHubView() {
  const { isLoggedIn, isLoading: authLoading, isPro } = useAuth();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }
    getUserDrafts()
      .then(data => setDrafts(data))
      .catch(err => setError(err.response?.data?.error?.message || 'Failed to load drafts'))
      .finally(() => setLoading(false));
  }, [authLoading, isLoggedIn]);

  function handleDeleteDraft(draftId) {
    setDrafts(prev => prev.filter(d => d.draftId !== draftId));
  }

  if (authLoading || loading) {
    return (
      <div className='loading'>
        <div className='spinner' />
        <p>Loading drafts...</p>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className='card' style={{ maxWidth: 480, margin: '48px auto', textAlign: 'center' }}>
        <h2>My Drafts</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
          Create a free account to start and save your draft progress.
        </p>
        <button
          className='btn btn-primary'
          onClick={() => navigate('/login?returnTo=/draft')}
        >
          Sign in to start drafting
        </button>
      </div>
    );
  }

  const activeDrafts = drafts.filter(d => d.status !== 'completed');
  const completedDrafts = drafts.filter(d => d.status === 'completed');

  return (
    <div className='card' style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>My Drafts</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className='mock-draft-cta-wrapper'>
            <button
              className={`btn btn-accent${!isPro ? ' btn--locked' : ''}`}
              onClick={() => isPro && navigate('/draft/new?mock=1')}
              disabled={!isPro}
              title={!isPro ? 'Upgrade to Pro to unlock Mock Drafts' : undefined}
            >
              ✦ Mock Draft
            </button>
            {!isPro && (
              <span className='mock-draft-cta-tooltip' role='tooltip'>
                Upgrade to Pro to unlock Mock Drafts
              </span>
            )}
          </div>
          <div className='mock-draft-cta-wrapper'>
            <button
              className={`btn btn-accent${!isPro ? ' btn--locked' : ''}`}
              onClick={() => isPro && navigate('/draft/commissioner/new')}
              disabled={!isPro}
              title={!isPro ? 'Upgrade to Pro to unlock Commissioner Mode' : undefined}
            >
              👑 Commissioner Mode
            </button>
            {!isPro && (
              <span className='mock-draft-cta-tooltip' role='tooltip'>
                Upgrade to Pro to unlock Commissioner Mode
              </span>
            )}
          </div>
          <button className='btn btn-secondary' onClick={() => navigate('/draft/join')}>
            Join a Draft
          </button>
          <button className='btn btn-primary' onClick={() => navigate('/draft/new')}>
            + New Draft
          </button>
        </div>
      </div>

      {error && <div className='error-banner'>{error}</div>}

      {activeDrafts.length > 0 && (
        <>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 8 }}>Active</p>
          {activeDrafts.map(d => (
            <DraftListItem key={d.draftId} draft={d} onDelete={handleDeleteDraft} />
          ))}
        </>
      )}

      {completedDrafts.length > 0 && (
        <>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 16, marginBottom: 8 }}>
            Completed
          </p>
          {completedDrafts.map(d => (
            <DraftListItem key={d.draftId} draft={d} onDelete={handleDeleteDraft} />
          ))}
        </>
      )}

      {drafts.length === 0 && (
        <div className='empty-state'>
          <h3>No drafts yet</h3>
          <p>Start a new draft to get going.</p>
        </div>
      )}
    </div>
  );
}

export default DraftHubView;
